import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productAPI, socialAPI, campaignAPI } from '../../services/api';
import { isValidProductId, resolveProductId } from '@/lib/productId';
import { normalizeAvailableNiches } from '@/lib/niches';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  TailAdminDetailRow,
  TailAdminFormSection,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from '@/components/template/tailadmin/DashboardPrimitives';

const steps = [
  { id: 1, title: 'Basic Info', description: 'Campaign details' },
  { id: 2, title: 'Goal & Commission', description: 'Set your rewards' },
  { id: 3, title: 'Requirements', description: 'BRM criteria' },
  { id: 4, title: 'Schedule', description: 'Optional timing' },
  { id: 5, title: 'Review', description: 'Confirm & create' }
];

const goalTypes = [
  { value: 'subscription', label: 'Subscription', description: 'Goal-plugin V1 verifies a customer subscription' }
];

const getProductId = (product) => resolveProductId(product);
const fieldClassName = "mt-1 border-gray-200 focus:border-brand-400";
const helperClassName = "mt-1 text-xs leading-5 text-gray-500";
const errorClassName = "mt-1 text-sm text-red-600";

const ClientCampaignCreate = () => {
  const navigate = useNavigate();
  const { productId: routeProductId } = useParams();
  const scopedToProduct = isValidProductId(routeProductId);
  const campaignsListPath = scopedToProduct
    ? `/client/products/${routeProductId}/campaigns`
    : '/client/campaigns';
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [availableNiches, setAvailableNiches] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    product_id: scopedToProduct ? routeProductId : '',
    title: '',
    description: '',
    target_url: '',
    goal_type: 'subscription',
    commission_per_goal: '',
    budget_cap: '',
    min_followers: '0',
    niches: [],
    starts_at: '',
    ends_at: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [productsRes, nichesRes] = await Promise.all([
        productAPI.getProducts(),
        socialAPI.getAvailableNiches()
      ]);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : productsRes.data.products || []);
      setAvailableNiches(normalizeAvailableNiches(nichesRes));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load required data');
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.product_id) newErrors.product_id = 'Please select a product';
      if (!formData.title || formData.title.length < 3) newErrors.title = 'Title must be at least 3 characters';
      if (!formData.description || formData.description.length < 10) newErrors.description = 'Description must be at least 10 characters';
      if (!formData.target_url) newErrors.target_url = 'Target URL is required';
      else if (!/^https?:\/\/.+/.test(formData.target_url)) newErrors.target_url = 'Must be a valid URL';
    }

    if (step === 2) {
      if (!formData.commission_per_goal || parseFloat(formData.commission_per_goal) <= 0) {
        newErrors.commission_per_goal = 'Commission must be greater than 0';
      }
      if (formData.budget_cap && parseFloat(formData.budget_cap) <= 0) {
        newErrors.budget_cap = 'Budget cap must be greater than 0';
      }
    }

    if (step === 3) {
      if (formData.min_followers && parseInt(formData.min_followers) < 0) {
        newErrors.min_followers = 'Cannot be negative';
      }
    }

    if (step === 4) {
      if (formData.starts_at && formData.ends_at) {
        if (new Date(formData.ends_at) <= new Date(formData.starts_at)) {
          newErrors.ends_at = 'End date must be after start date';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setLoading(true);
    try {
      const payload = {
        product_id: formData.product_id,
        title: formData.title,
        description: formData.description,
        target_url: formData.target_url,
        goal_type: formData.goal_type,
        commission_per_goal: parseFloat(formData.commission_per_goal),
        budget_cap: formData.budget_cap ? parseFloat(formData.budget_cap) : null,
        min_followers: parseInt(formData.min_followers) || 0,
        niches: formData.niches,
        starts_at: formData.starts_at || null,
        ends_at: formData.ends_at || null
      };

      await campaignAPI.createCampaign(payload);
      toast.success('Prove-Request created successfully!');
      navigate(campaignsListPath);
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };

  const toggleNiche = (nicheSlug) => {
    setFormData(prev => ({
      ...prev,
      niches: prev.niches.includes(nicheSlug)
        ? prev.niches.filter(n => n !== nicheSlug)
        : [...prev.niches, nicheSlug]
    }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="product_id" className="text-sm font-medium text-gray-700">Existing Offer *</Label>
              <Select
                value={formData.product_id}
                onValueChange={(value) => setFormData({ ...formData, product_id: value })}
                disabled={scopedToProduct}
              >
                <SelectTrigger className={fieldClassName}>
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(product => (
                    <SelectItem key={getProductId(product)} value={getProductId(product)}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.product_id && <p className={errorClassName}>{errors.product_id}</p>}
            </div>

            {formData.product_id ? (() => {
              const selectedOffer = products.find(product => getProductId(product) === formData.product_id);
              const requestType = selectedOffer?.request_type || selectedOffer?.requestType;
              const directVersion = selectedOffer?.direct_version || selectedOffer?.directVersion;
              return (
                <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4">
                  <p className="text-sm font-semibold text-gray-900">Inherited Offer setup</p>
                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    This push inherits the Offer's {requestType ? requestType : 'configured'} request model
                    {directVersion ? ` (${directVersion.toUpperCase()})` : ''}, Subscription V1 goal, connection endpoints, and payout policy.
                  </p>
                  <p className="mt-2 text-xs text-gray-500">Each push still needs its own contract test and, for Direct V2, a campaign registration form before publishing.</p>
                </div>
              );
            })() : null}

            <div>
              <Label htmlFor="title" className="text-sm font-medium text-gray-700">Prove-Request Title *</Label>
              <Input
                id="title"
                placeholder="e.g., Spring Promotion 2025"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className={fieldClassName}
              />
              {errors.title && <p className={errorClassName}>{errors.title}</p>}
            </div>

            <div>
              <Label htmlFor="description" className="text-sm font-medium text-gray-700">Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe your campaign, what BRMs should know, and what they're promoting..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className={fieldClassName}
              />
              {errors.description && <p className={errorClassName}>{errors.description}</p>}
            </div>

            <div>
              <Label htmlFor="target_url" className="text-sm font-medium text-gray-700">Target URL *</Label>
              <Input
                id="target_url"
                placeholder="https://yoursite.com/landing"
                value={formData.target_url}
                onChange={(e) => setFormData({ ...formData, target_url: e.target.value })}
                className={fieldClassName}
              />
              <p className={helperClassName}>Where users will be redirected after clicking the BRM's link</p>
              {errors.target_url && <p className={errorClassName}>{errors.target_url}</p>}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="goal_type" className="text-sm font-medium text-gray-700">Goal Type *</Label>
              <Select value={formData.goal_type} onValueChange={(value) => setFormData({ ...formData, goal_type: value })}>
                <SelectTrigger className={fieldClassName}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {goalTypes.map(goal => (
                    <SelectItem key={goal.value} value={goal.value}>
                      <div>
                        <div className="font-medium">{goal.label}</div>
                        <div className="text-xs text-gray-500">{goal.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className={helperClassName}>Additional goals will be added as independent plugins with their own request and tracking forms.</p>
            </div>

            <div>
              <Label htmlFor="commission_per_goal" className="text-sm font-medium text-gray-700">Commission per Goal (₦) *</Label>
              <Input
                id="commission_per_goal"
                type="number"
                placeholder="5000"
                value={formData.commission_per_goal}
                onChange={(e) => setFormData({ ...formData, commission_per_goal: e.target.value })}
                className={fieldClassName}
                min="0"
                step="0.01"
              />
              <p className={helperClassName}>How much BRMs earn per conversion</p>
              {errors.commission_per_goal && <p className={errorClassName}>{errors.commission_per_goal}</p>}
            </div>

            <div>
              <Label htmlFor="budget_cap" className="text-sm font-medium text-gray-700">Budget Cap (₦) - Optional</Label>
              <Input
                id="budget_cap"
                type="number"
                placeholder="Leave empty for unlimited"
                value={formData.budget_cap}
                onChange={(e) => setFormData({ ...formData, budget_cap: e.target.value })}
                className={fieldClassName}
                min="0"
                step="0.01"
              />
              <p className={helperClassName}>Campaign stops when budget is reached</p>
              {errors.budget_cap && <p className={errorClassName}>{errors.budget_cap}</p>}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="min_followers" className="text-sm font-medium text-gray-700">Minimum Followers</Label>
              <Input
                id="min_followers"
                type="number"
                placeholder="0"
                value={formData.min_followers}
                onChange={(e) => setFormData({ ...formData, min_followers: e.target.value })}
                className={fieldClassName}
                min="0"
              />
              <p className={helperClassName}>Minimum follower count required (0 = no requirement)</p>
              {errors.min_followers && <p className={errorClassName}>{errors.min_followers}</p>}
            </div>

            <div>
              <Label className="text-sm font-medium text-gray-700">Target Niches (Optional)</Label>
              <p className="mb-3 mt-1 text-xs leading-5 text-gray-500">Select niches that match your campaign. BRMs will see this as a match if they're in these niches.</p>
              <div className="flex flex-wrap gap-2">
                {availableNiches.map(({ slug, label }) => (
                  <button
                    type="button"
                    key={slug}
                    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold transition ${
                      formData.niches.includes(slug)
                        ? 'border-brand-200 bg-brand-50 text-brand-500'
                        : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                    }`}
                    onClick={() => toggleNiche(slug)}
                  >
                    {formData.niches.includes(slug) && <Check className="w-3 h-3 mr-1" />}
                    {label}
                  </button>
                ))}
              </div>
              {formData.niches.length > 0 && (
                <p className="mt-2 text-xs text-emerald-600">{formData.niches.length} niches selected</p>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div>
              <Label htmlFor="starts_at" className="text-sm font-medium text-gray-700">Start Date (Optional)</Label>
              <Input
                id="starts_at"
                type="datetime-local"
                value={formData.starts_at}
                onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                className={fieldClassName}
              />
              <p className={helperClassName}>Leave empty to start immediately</p>
            </div>

            <div>
              <Label htmlFor="ends_at" className="text-sm font-medium text-gray-700">End Date (Optional)</Label>
              <Input
                id="ends_at"
                type="datetime-local"
                value={formData.ends_at}
                onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                className={fieldClassName}
              />
              <p className={helperClassName}>Leave empty for no end date</p>
              {errors.ends_at && <p className={errorClassName}>{errors.ends_at}</p>}
            </div>
          </div>
        );

      case 5:
        const selectedProduct = products.find(p => getProductId(p) === formData.product_id);
        return (
          <div className="space-y-4">
            <TailAdminPanel title="Prove-Request Summary" description="Review this new push on your existing Offer." action={null}>
              <div className="grid gap-3 sm:grid-cols-2">
                <TailAdminDetailRow label="Product" value={selectedProduct?.name || 'N/A'} />
                <TailAdminDetailRow label="Goal Type" value={<span className="capitalize">{formData.goal_type}</span>} />
                <TailAdminDetailRow label="Commission" value={`₦${parseFloat(formData.commission_per_goal || 0).toLocaleString()}`} />
                <TailAdminDetailRow label="Budget Cap" value={formData.budget_cap ? `₦${parseFloat(formData.budget_cap).toLocaleString()}` : 'Unlimited'} />
                <TailAdminDetailRow label="Min Followers" value={parseInt(formData.min_followers || 0).toLocaleString()} />
                <TailAdminDetailRow label="Niches" value={formData.niches.length > 0 ? formData.niches.length : 'All'} />
              </div>

              <div className="mt-4 space-y-3">
                <TailAdminDetailRow label="Title" value={formData.title} />
                <div className="rounded-xl border border-gray-100 p-3">
                  <p className="text-sm font-medium text-gray-700">Description</p>
                  <p className="mt-1 text-sm leading-6 text-gray-500">{formData.description}</p>
                </div>
                <div className="rounded-xl border border-gray-100 p-3">
                  <p className="text-sm font-medium text-gray-700">Target URL</p>
                  <p className="mt-1 break-all text-sm font-medium text-brand-500">{formData.target_url}</p>
                </div>

                {(formData.starts_at || formData.ends_at) && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {formData.starts_at && (
                      <TailAdminDetailRow label="Starts" value={new Date(formData.starts_at).toLocaleString()} />
                    )}
                    {formData.ends_at && (
                      <TailAdminDetailRow label="Ends" value={new Date(formData.ends_at).toLocaleString()} />
                    )}
                  </div>
                )}
              </div>
            </TailAdminPanel>

            <div className="rounded-2xl border border-brand-200 bg-brand-50 p-4">
              <p className="text-sm leading-6 text-gray-600">
                <strong>Note:</strong> This Prove-Request starts as <strong>draft</strong>. Fund its escrow, then publish it for matching BRMs.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 sm:px-6">
      <TailAdminPageHeader
        eyebrow="Existing Offer"
        title="New Prove-Request push"
        description="Launch another funded push on an Offer you have already configured."
        actions={
          <Button variant="outline" onClick={() => navigate(campaignsListPath)}>
            Cancel
          </Button>
        }
      />

      {/* Progress Steps */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs sm:p-5">
        <div className="grid gap-3 md:grid-cols-5">
        {steps.map((step) => (
          <button
            key={step.id}
            type="button"
            onClick={() => {
              if (step.id < currentStep || validateStep(currentStep)) {
                setCurrentStep(step.id);
              }
            }}
            className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${
              currentStep === step.id
                ? 'border-brand-200 bg-brand-50'
                : currentStep > step.id
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-sm font-semibold transition-colors ${
                  currentStep >= step.id
                    ? 'bg-brand-500 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {currentStep > step.id ? <Check className="w-5 h-5" /> : step.id}
              </div>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-gray-900">{step.title}</span>
                <span className="block truncate text-xs text-gray-500">{step.description}</span>
              </span>
          </button>
        ))}
        </div>
        <div className="mt-5 h-2 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-brand-500 transition-all"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Step Content */}
      <TailAdminFormSection
        title={steps[currentStep - 1].title}
        description={steps[currentStep - 1].description}
        className="max-w-3xl"
      >
        <div className="mb-5">
          <TailAdminStatusBadge tone="primary">
            Step {currentStep} of {steps.length}
          </TailAdminStatusBadge>
        </div>
        {renderStepContent()}
      </TailAdminFormSection>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-4 shadow-theme-xs">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        {currentStep < 5 ? (
          <Button onClick={nextStep}>
            Next
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create Prove-Request'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ClientCampaignCreate;
